/**
 * ForBlock — For 循环块渲染（替代 ForBlockRenderer 的 810 行平行管线）。
 *
 * 核心改进：每次迭代通过 ScopeProvider + BlockList 复用主渲染管线，
 * 不再维护 ForIterationRenderChild / ForIterationInlineRef / ForIterationRenderBlock。
 *
 * 预处理 markdown 块中的 $each 引用（substituteLoopVars），
 * InlineRef 通过 ScopeContext.loopVar 自动解析 $each 引用。
 */

import type {
    ContentDefinition,
    ForBlock as ForBlockType,
    ForDefinition,
    ForOutput,
    ForOutputMetadata,
    PluginDefinition,
    PluginOutput,
    RenderMode,
} from "@magic-md/core";
import { useT } from "@magic-md/i18n/react";
import {
    ChevronDown,
    ChevronRight,
    Loader2,
    Maximize2,
    Minimize2,
    Play,
    RefreshCw,
    Repeat,
    Square,
    StretchHorizontal,
} from "lucide-react";
import {
    type CSSProperties,
    type MouseEvent,
    type ReactNode,
    createElement,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useJustFinishedFlash } from "../../../hooks/useJustFinishedFlash";
import { resolveImportedSymbolNamespace } from "../../../preview-logic";
import { cn } from "../../../lib/utils";
import {
    PREVIEW_HORIZONTAL_PADDING,
    PREVIEW_WIDTH_PRESETS,
    type PreviewWidthControl,
    clampPreviewViewportWidth,
    resolvePreviewViewportWidth,
} from "../../../lib/preview-width";
import type {
    ForExecutionEntry,
    ForIterationEntry,
} from "../../../store/plugin-execution-store";
import {
    getThemeAccentStyle,
    getThemeAccentValue,
} from "../../../theme/runtime/theme-accents";
import { AnnotationBlockFooterControl } from "../../annotations/AnnotationBlockTarget";
import { buildAnnotationOutputHash } from "../../annotations/hash";
import {
    findOutputInScope,
    ScopeProvider,
    useScopeContext,
} from "../../ScopeContext";
import { useDocContext } from "../contexts";
import { useForExecution, useImportExecution } from "../contexts/ExecutionContext";
import { substituteLoopVars } from "../utils/loop-vars";
import { BlockList } from "./BlockList";
import {
    CollapsedUnitRenderHeader,
    type CollapsedUnitRenderStatus,
} from "./CollapsedUnitRenderHeader";
import { readRenderExpanded } from "./render-attrs";
import { RenderUnitFooterDisclosure } from "./RenderUnitFooterDisclosure";
import {
    type UnitFooterCopyAction,
    stringifyUnitFooterJson,
    UnitFooterCopyButton,
} from "./UnitFooterCopyButton";
import { UnitDependencyFooterButton } from "./UnitDependencyFooterButton";
import { TemplateRenderPlaceholder } from "./TemplateRenderPlaceholder";
import { TemplateUsageWarning } from "./TemplateUsageWarning";
import { RenderUnitBadge } from "./RenderUnitBadge";

const WORKSPACE_FULLSCREEN_SELECTOR =
    "[data-magic-editor-preview-workspace='true']";

const renderInnerBlockItem = ({ children }: { children: ReactNode }) => (
    <div className="magic-render-inner-block-item">{children}</div>
);

type WorkspaceFullscreenRect = {
    top: number;
    left: number;
    width: number;
    height: number;
};

function formatPreviewWidthLabel(
    t: ReturnType<typeof useT>,
    control: PreviewWidthControl,
    customWidth: number | null,
): string {
    if (control === "custom") {
        return customWidth
            ? t("previewWidth.customWithWidth", {
                width: Math.round(customWidth),
            })
            : t("previewWidth.custom");
    }
    return t(`previewWidth.${control}`);
}

function findWorkspaceFullscreenHost(
    element: HTMLElement | null,
): HTMLElement | null {
    return element?.closest<HTMLElement>(WORKSPACE_FULLSCREEN_SELECTOR) ?? null;
}

function readWorkspaceFullscreenRect(
    host: HTMLElement | null,
): WorkspaceFullscreenRect | null {
    if (typeof window === "undefined") return null;

    if (host) {
        const rect = host.getBoundingClientRect();
        return {
            top: Math.round(rect.top),
            left: Math.round(rect.left),
            width: Math.round(rect.width),
            height: Math.round(rect.height),
        };
    }

    const visualViewport = window.visualViewport;
    return {
        top: Math.round(visualViewport?.offsetTop ?? 0),
        left: Math.round(visualViewport?.offsetLeft ?? 0),
        width: Math.round(visualViewport?.width ?? window.innerWidth),
        height: Math.round(visualViewport?.height ?? window.innerHeight),
    };
}

function readFullscreenAvailableWidth(
    rect: WorkspaceFullscreenRect | null,
): number | null {
    if (rect) return Math.max(1, rect.width - PREVIEW_HORIZONTAL_PADDING);
    if (typeof window === "undefined") return null;
    return Math.max(1, window.innerWidth - PREVIEW_HORIZONTAL_PADDING);
}

function escapeForAttr(value: unknown): string {
    return String(value).replace(/\\/g, "\\\\").replace(/"/g, '\\"');
}

function buildForConfig(block: ForBlockType): Record<string, unknown> {
    return {
        each: block.each,
        of: block.of,
        ...(block.limit != null ? { limit: block.limit } : {}),
        ...(block.offset != null ? { offset: block.offset } : {}),
        ...(block.concurrency != null
            ? { concurrency: block.concurrency }
            : {}),
        ...(block.template ? { template: true } : {}),
    };
}

function buildForSnippet(block: ForBlockType): string {
    const attrs = [
        `each="${escapeForAttr(block.each)}"`,
        `of="${escapeForAttr(block.of)}"`,
        ...(block.limit != null ? [`limit="${block.limit}"`] : []),
        ...(block.offset != null ? [`offset="${block.offset}"`] : []),
        ...(block.concurrency != null
            ? [`concurrency="${block.concurrency}"`]
            : []),
        ...(block.template ? ['template="true"'] : []),
    ];
    return [`::for[${block.id}]{${attrs.join(" ")}}`, "::endfor"].join("\n");
}

function collectScopedForDefinitions(
    blocks: ForBlockType["children"],
): Map<string, ForDefinition> {
    const definitions = new Map<string, ForDefinition>();

    for (const child of blocks) {
        if (child.type !== "for-block") continue;
        const nestedForDefinitions = collectScopedForDefinitions(
            child.children,
        );
        definitions.set(child.id, {
            id: child.id,
            type: "for",
            each: child.each,
            of: child.of,
            limit: child.limit,
            offset: child.offset,
            concurrency: child.concurrency,
            template: child.template,
            params: child.params,
            innerScope: {
                definitions: new Map(),
                contentDefinitions: new Map(),
                forDefinitions: nestedForDefinitions,
                blocks: child.children,
            },
        });
    }

    return definitions;
}

export function ForBlock({
    block,
    mode = "block",
    attrs,
    defaultExpanded = true,
}: {
    block: ForBlockType;
    mode?: RenderMode;
    attrs?: Record<string, string>;
    defaultExpanded?: boolean;
}) {
    const t = useT("editor");
    const doc = useDocContext();
    const scope = useScopeContext();
    const directExecution = useForExecution(block.id);
    const importNamespace = useMemo(
        () =>
            resolveImportedSymbolNamespace(
                block.id,
                doc.importBindings,
                doc.localExecutionUnitIds,
            ),
        [block.id, doc.importBindings, doc.localExecutionUnitIds],
    );
    const importExecution = useImportExecution(importNamespace);
    const isImportRunning = importExecution?.status === "running";
    const canRunImport = !!importNamespace && !!doc.onRunImport;
    const handleRunImport = useCallback((event?: MouseEvent<HTMLElement>) => {
        event?.stopPropagation();
        if (!canRunImport || !importNamespace || isImportRunning) return;
        doc.onRunImport?.(importNamespace);
    }, [canRunImport, doc.onRunImport, importNamespace, isImportRunning]);
    const renderContainerRef = useRef<HTMLDivElement | null>(null);
    const renderBodyRef = useRef<HTMLDivElement | null>(null);
    const fullscreenResizeRef = useRef<{
        side: "left" | "right";
        startX: number;
        startWidth: number;
    } | null>(null);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [workspaceFullscreenRect, setWorkspaceFullscreenRect] =
        useState<WorkspaceFullscreenRect | null>(null);
    const [fullscreenWidthPreset, setFullscreenWidthPreset] =
        useState<PreviewWidthControl>("fill");
    const [customFullscreenWidth, setCustomFullscreenWidth] = useState<
        number | null
    >(null);
    const [isFullscreenWidthMenuOpen, setIsFullscreenWidthMenuOpen] =
        useState(false);
    const [isFullscreenWidthDragging, setIsFullscreenWidthDragging] =
        useState(false);
    const deferredOutputRequestKeyRef = useRef<string | null>(null);
    const [isLoadingDeferredOutput, setIsLoadingDeferredOutput] =
        useState(false);
    const [deferredOutputError, setDeferredOutputError] =
        useState<string | null>(null);

    // Scope-based fallback: nested for-blocks store their output in the parent
    // iteration's unitOutputs, not in global forExecutions (during hydration).
    const scopeFallback = useMemo<ForExecutionEntry | undefined>(() => {
        if (directExecution || !scope) return undefined;
        const raw = findOutputInScope(scope, block.id);
        if (
            !raw ||
            !Array.isArray(
                (raw as unknown as Record<string, unknown>).iterations,
            )
        )
            return undefined;
        const forOut = raw as unknown as ForOutput;
        const forMeta = forOut.__metadata as ForOutputMetadata | undefined;
        return {
            forId: block.id,
            status:
                (forMeta?.failed ?? 0) > 0 && (forMeta?.succeeded ?? 0) === 0
                    ? "error"
                    : "success",
            totalIterations: forMeta?.total ?? forOut.iterations.length,
            completedIterations: forOut.iterations.length,
            iterations: forOut.iterations.map(
                (iter, idx): ForIterationEntry => ({
                    index: idx,
                    item: iter.item,
                    outputs: iter.outputs ?? {},
                    definitions: iter.definitions,
                    contentDefinitions: iter.contentDefinitions,
                    success: iter.success ?? true,
                    error: iter.error,
                }),
            ),
            startedAt: null,
            duration: forMeta?.duration ?? null,
            meta: forMeta
                ? {
                      total: forMeta.total,
                      succeeded: forMeta.succeeded,
                      failed: forMeta.failed,
                      duration: forMeta.duration,
                      startedAt: forMeta.startedAt,
                  }
                : undefined,
        };
    }, [directExecution, scope, block.id]);

    const forExecution = directExecution ?? scopeFallback;
    const useExternalRef = useMemo(
        () =>
            doc.useBindings?.find((binding) => binding.id === block.id)
                ?.externalRef,
        [block.id, doc.useBindings],
    );
    const deferredExternalRef = block.externalRef ?? useExternalRef;
    const deferredOutputRequestTarget = deferredExternalRef || importNamespace
        ? block.id
        : null;
    const status = forExecution?.status ?? "idle";
    const justFinished = useJustFinishedFlash(status, {
        suppress: status === "error",
    });
    const hasIterations = !!forExecution && forExecution.iterations.length > 0;
    const isTemplateFor = !!block.template;
    const initialExpanded = readRenderExpanded(attrs, defaultExpanded);
    const hasExplicitExpandedAttr = attrs?.expanded != null;
    const [expanded, setExpanded] = useState(initialExpanded);
    const [collapsedIterationIndexes, setCollapsedIterationIndexes] = useState<
        Set<number>
    >(() => new Set());
    const canRunFor = doc.executable && !scope && !!doc.onRunFor;
    const canCancelFor = doc.executable && !scope && !!doc.onCancelFor;
    const allIterationsExpanded =
        !forExecution || forExecution.iterations.length === 0
            ? true
            : forExecution.iterations.every(
                  (iter) => !collapsedIterationIndexes.has(iter.index),
              );
    const toggleAllIterations = useCallback(() => {
        if (!forExecution || forExecution.iterations.length === 0) return;
        setCollapsedIterationIndexes(
            allIterationsExpanded
                ? new Set(forExecution.iterations.map((iter) => iter.index))
                : new Set(),
        );
    }, [allIterationsExpanded, forExecution]);
    const toggleIteration = useCallback((index: number) => {
        setCollapsedIterationIndexes((prev) => {
            const next = new Set(prev);
            if (next.has(index)) {
                next.delete(index);
            } else {
                next.add(index);
            }
            return next;
        });
    }, []);

    useEffect(() => {
        setExpanded(initialExpanded);
    }, [block.id, initialExpanded]);

    useEffect(() => {
        if (!forExecution) return;
        setIsLoadingDeferredOutput(false);
        setDeferredOutputError(null);
    }, [forExecution]);

    useEffect(() => {
        if (forExecution || !deferredOutputRequestTarget || !doc.onLoadOutput) {
            return;
        }

        const requestKey = deferredExternalRef
            ? `${block.id}\u0000${deferredExternalRef.protocol}\u0000${deferredExternalRef.locator}`
            : `${block.id}\u0000import-symbol\u0000${importNamespace ?? ""}`;
        if (deferredOutputRequestKeyRef.current === requestKey) return;
        deferredOutputRequestKeyRef.current = requestKey;

        let cancelled = false;
        setIsLoadingDeferredOutput(true);
        setDeferredOutputError(null);

        doc.onLoadOutput(deferredOutputRequestTarget)
            .catch((err) => {
                if (cancelled) return;
                setDeferredOutputError(
                    err instanceof Error ? err.message : String(err),
                );
            })
            .finally(() => {
                if (!cancelled) setIsLoadingDeferredOutput(false);
            });

        return () => {
            cancelled = true;
        };
    }, [
        block.id,
        deferredExternalRef,
        deferredOutputRequestTarget,
        doc.onLoadOutput,
        forExecution,
        importNamespace,
    ]);

    useEffect(() => {
        if (!isFullscreen || typeof window === "undefined") return;

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key !== "Escape") return;
            if (isFullscreenWidthMenuOpen) {
                setIsFullscreenWidthMenuOpen(false);
                return;
            }
            setIsFullscreen(false);
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [isFullscreen, isFullscreenWidthMenuOpen]);

    useEffect(() => {
        if (!isFullscreen) {
            setWorkspaceFullscreenRect(null);
            setIsFullscreenWidthMenuOpen(false);
            setIsFullscreenWidthDragging(false);
            return;
        }

        const container = renderContainerRef.current;
        const workspaceHost = findWorkspaceFullscreenHost(container);

        const updateRect = () => {
            const next = readWorkspaceFullscreenRect(workspaceHost);
            if (!next) return;
            setWorkspaceFullscreenRect((current) =>
                current?.top === next.top &&
                current?.left === next.left &&
                current?.width === next.width &&
                current?.height === next.height
                    ? current
                    : next,
            );
        };

        updateRect();
        window.addEventListener("resize", updateRect);
        window.addEventListener("scroll", updateRect, true);

        if (typeof ResizeObserver === "undefined" || !workspaceHost) {
            return () => {
                window.removeEventListener("resize", updateRect);
                window.removeEventListener("scroll", updateRect, true);
            };
        }

        const observer = new ResizeObserver(updateRect);
        observer.observe(workspaceHost);
        return () => {
            window.removeEventListener("resize", updateRect);
            window.removeEventListener("scroll", updateRect, true);
            observer.disconnect();
        };
    }, [isFullscreen]);

    const fullscreenAvailableWidth = isFullscreen
        ? readFullscreenAvailableWidth(workspaceFullscreenRect)
        : null;
    const resolvedFullscreenViewportWidth = isFullscreen
        ? resolvePreviewViewportWidth(
              fullscreenWidthPreset,
              customFullscreenWidth,
              fullscreenAvailableWidth,
          )
        : "fill";
    const currentFullscreenWidthLabel = formatPreviewWidthLabel(
        t,
        fullscreenWidthPreset,
        customFullscreenWidth,
    );

    const handleToggleFullscreen = useCallback(
        (event: MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation();
            if (isFullscreen) {
                setIsFullscreen(false);
                return;
            }

            const workspaceHost = findWorkspaceFullscreenHost(
                renderContainerRef.current,
            );
            setWorkspaceFullscreenRect(
                readWorkspaceFullscreenRect(workspaceHost),
            );
            setFullscreenWidthPreset("fill");
            setCustomFullscreenWidth(null);
            setIsFullscreenWidthMenuOpen(false);
            setIsFullscreen(true);
        },
        [isFullscreen],
    );

    const handleFullscreenWidthPresetSelect = useCallback(
        (preset: Exclude<PreviewWidthControl, "custom">) => {
            setFullscreenWidthPreset(preset);
            if (preset === "fill") setCustomFullscreenWidth(null);
            setIsFullscreenWidthMenuOpen(false);
        },
        [],
    );

    const startFullscreenWidthResize = useCallback(
        (side: "left" | "right", event: MouseEvent<HTMLButtonElement>) => {
            event.preventDefault();
            event.stopPropagation();
            const rect = renderBodyRef.current?.getBoundingClientRect();
            const currentWidth =
                rect?.width ??
                (resolvedFullscreenViewportWidth === "fill"
                    ? fullscreenAvailableWidth
                    : resolvedFullscreenViewportWidth);
            if (!currentWidth) return;
            fullscreenResizeRef.current = {
                side,
                startX: event.clientX,
                startWidth: currentWidth,
            };
            setFullscreenWidthPreset("custom");
            setIsFullscreenWidthMenuOpen(false);
            setIsFullscreenWidthDragging(true);
        },
        [fullscreenAvailableWidth, resolvedFullscreenViewportWidth],
    );

    const resetFullscreenWidth = useCallback(() => {
        setFullscreenWidthPreset("fill");
        setCustomFullscreenWidth(null);
        setIsFullscreenWidthMenuOpen(false);
    }, []);

    useEffect(() => {
        if (!isFullscreenWidthDragging || typeof window === "undefined") return;

        const onMove = (event: globalThis.MouseEvent) => {
            const state = fullscreenResizeRef.current;
            const availableWidth = readFullscreenAvailableWidth(
                workspaceFullscreenRect,
            );
            if (!state || !availableWidth) return;
            const delta = event.clientX - state.startX;
            const nextWidth =
                state.side === "right"
                    ? state.startWidth + delta
                    : state.startWidth - delta;
            setFullscreenWidthPreset("custom");
            setCustomFullscreenWidth(
                clampPreviewViewportWidth(nextWidth, availableWidth),
            );
        };

        const onUp = () => {
            fullscreenResizeRef.current = null;
            setIsFullscreenWidthDragging(false);
        };

        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseup", onUp);
        document.body.style.cursor = "col-resize";
        document.body.style.userSelect = "none";

        return () => {
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseup", onUp);
            document.body.style.cursor = "";
            document.body.style.userSelect = "";
        };
    }, [isFullscreenWidthDragging, workspaceFullscreenRect]);

    const renderBodyStyle = isFullscreen
        ? ({
              width:
                  resolvedFullscreenViewportWidth === "fill"
                      ? "100%"
                      : `${resolvedFullscreenViewportWidth}px`,
              maxWidth: "100%",
          } as CSSProperties)
        : undefined;

    const fullscreenContainerStyle =
        isFullscreen && workspaceFullscreenRect
            ? ({
                  top: workspaceFullscreenRect.top,
                  left: workspaceFullscreenRect.left,
                  width: workspaceFullscreenRect.width,
                  height: workspaceFullscreenRect.height,
              } as CSSProperties)
            : undefined;

    const copyReferencePath = `${block.id}.output`;
    const copyReferenceText = `$ref{${copyReferencePath}}`;
    const forConfig = useMemo(
        () => buildForConfig(block),
        [
            block.concurrency,
            block.each,
            block.limit,
            block.of,
            block.offset,
            block.template,
        ],
    );
    const copyableForOutput = useMemo(() => {
        if (!forExecution) return undefined;
        return {
            status: forExecution.status,
            totalIterations: forExecution.totalIterations,
            completedIterations: forExecution.completedIterations,
            duration: forExecution.duration,
            error: forExecution.error,
            meta: forExecution.meta,
            iterations: forExecution.iterations.map((iteration) => ({
                index: iteration.index,
                item: iteration.item,
                outputs: iteration.outputs,
                success: iteration.success,
                error: iteration.error,
            })),
        };
    }, [forExecution]);
    const copyActions = useMemo<UnitFooterCopyAction[]>(
        () => [
            {
                key: "copy-reference",
                label: t("copy.reference"),
                text: copyReferenceText,
                icon: "reference",
            },
            {
                key: "copy-path",
                label: t("copy.path"),
                text: copyReferencePath,
                icon: "path",
            },
            {
                key: "copy-id",
                label: t("copy.id"),
                text: block.id,
                icon: "id",
            },
            {
                key: "copy-config",
                label: t("copy.config"),
                text: stringifyUnitFooterJson(forConfig),
                icon: "config",
                separator: true,
            },
            {
                key: "copy-source",
                label: t("copy.loopSource"),
                text: block.of,
                icon: "path",
            },
            ...(copyableForOutput
                ? [
                          {
                          key: "copy-output",
                          label: t("copy.output"),
                          text: stringifyUnitFooterJson(copyableForOutput),
                          icon: "output" as const,
                          separator: true,
                      },
                  ]
                : []),
            {
                key: "copy-for-snippet",
                label: t("copy.forSnippet"),
                text: buildForSnippet(block),
                icon: "snippet",
                separator: true,
            },
            ...(doc.documentPath
                ? [
                          {
                          key: "copy-document-path",
                          label: t("copy.documentPath"),
                          text: doc.documentPath,
                          icon: "document" as const,
                          separator: true,
                      },
                  ]
                : []),
        ],
        [
            block,
            block.id,
            block.of,
            copyReferencePath,
            copyReferenceText,
            copyableForOutput,
            doc.documentPath,
            forConfig,
            t,
        ],
    );
    const annotationForOutput = useMemo(() => {
        if (!forExecution) return undefined;
        return {
            status: forExecution.status,
            totalIterations: forExecution.totalIterations,
            completedIterations: forExecution.completedIterations,
            error: forExecution.error,
            iterations: forExecution.iterations.map((iteration) => ({
                index: iteration.index,
                item: iteration.item,
                outputs: iteration.outputs,
                success: iteration.success,
                error: iteration.error,
            })),
            __metadata: {
                unitType: "for",
                actionName: "iterate",
                duration: forExecution.duration ?? undefined,
                total: forExecution.meta?.total ?? forExecution.totalIterations,
                succeeded: forExecution.meta?.succeeded,
                failed: forExecution.meta?.failed,
                startedAt:
                    forExecution.meta?.startedAt ?? forExecution.startedAt,
            },
        };
    }, [forExecution]);
    const outputHash = useMemo(
        () =>
            buildAnnotationOutputHash({
                output: annotationForOutput,
                externalRef: deferredExternalRef,
            }),
        [annotationForOutput, deferredExternalRef],
    );

    if (isTemplateFor) {
        return (
            <TemplateRenderPlaceholder
                unitId={block.id}
                kindLabel="For"
                technicalLabel={`each=${block.each}`}
                params={block.params}
                mode={mode}
                badgeMain="F"
                badgeSub="T"
            />
        );
    }

    if (block.params && block.params.length > 0) {
        return (
            <TemplateUsageWarning
                unitId={block.id}
                kindLabel={t("render.kind.forLoop")}
                technicalLabel={`for each=${block.each} of=${block.of}`}
                params={block.params}
                mode={mode}
                badge={{ main: "F" }}
                locationLabel="for"
            />
        );
    }

    // ── Inline mode ──
    if (mode === "inline") {
        return (
            <ForBlockInlineState
                block={block}
                forExecution={forExecution}
                executable={doc.executable}
                onRunFor={scope ? undefined : doc.onRunFor}
                onCancelFor={scope ? undefined : doc.onCancelFor}
            />
        );
    }

    if (hasExplicitExpandedAttr && !expanded) {
        const collapsedStatus: CollapsedUnitRenderStatus =
            isImportRunning || isLoadingDeferredOutput
                ? "running"
                : status === "error" || deferredOutputError
                    ? "error"
                    : status === "running"
                        ? "running"
                        : status === "success" || hasIterations
                            ? "success"
                            : null;
        return (
            <div className="relative my-2">
                <CollapsedUnitRenderHeader
                    unitId={block.id}
                    label={t("render.kind.forLoop")}
                    technicalLabel={`for each=${block.each} of=${block.of}`}
                    status={collapsedStatus}
                    statusLabels={{
                        running: t("status.running"),
                        success: t("render.status.ran"),
                        error: deferredOutputError ?? forExecution?.error ?? t("render.status.failed"),
                    }}
                    onExpand={() => setExpanded(true)}
                />
            </div>
        );
    }

    if (status === "idle" && !hasIterations && !isImportRunning) {
        return (
            <ForRenderEmptyState
                block={block}
                canRun={canRunFor}
                onRun={() => doc.onRunFor?.(block.id)}
                isLoading={isLoadingDeferredOutput}
                errorMessage={deferredOutputError}
            />
        );
    }

    if (!expanded) {
        const collapsedStatus: CollapsedUnitRenderStatus =
            isImportRunning || isLoadingDeferredOutput
                ? "running"
                : status === "error" || deferredOutputError
                    ? "error"
                    : status === "running"
                        ? "running"
                        : status === "success" || hasIterations
                            ? "success"
                            : null;
        return (
            <div className="relative my-2">
                <CollapsedUnitRenderHeader
                    unitId={block.id}
                    label={t("render.kind.forLoop")}
                    technicalLabel={`for each=${block.each} of=${block.of}`}
                    status={collapsedStatus}
                    statusLabels={{
                        running: t("status.running"),
                        success: t("render.status.ran"),
                        error: deferredOutputError ?? forExecution?.error ?? t("render.status.failed"),
                    }}
                    onExpand={() => setExpanded(true)}
                />
            </div>
        );
    }

    const forBlockFrame = (
        <div className="relative rounded-lg border border-border overflow-hidden bg-background">
            {/* Running shimmer */}
            {(status === "running" ||
                isLoadingDeferredOutput ||
                isImportRunning) && (
                <>
                    <div
                        className="absolute inset-x-0 top-0 z-10 h-[2px] overflow-hidden rounded-t-lg theme-accent-progress-track"
                        style={getThemeAccentStyle(isImportRunning ? "running" : "for")}
                    >
                        <div
                            className="h-full w-2/5 rounded-full running-shimmer"
                            style={{
                                background:
                                    "linear-gradient(to right, transparent, var(--theme-accent), transparent)",
                                ...getThemeAccentStyle(isImportRunning ? "running" : "for"),
                            }}
                        />
                    </div>
                    <div className="absolute inset-0 bg-background/20 z-[9] rounded-lg pointer-events-none" />
                </>
            )}

            {/* Header */}
            <div
                role="button"
                tabIndex={0}
                className="relative z-10 w-full flex items-center gap-2 px-3 py-1.5 bg-muted/40 border-b border-border text-left hover:bg-muted/60 transition-colors"
                onClick={() => setExpanded((v) => !v)}
                onKeyDown={(event) => {
                    if (event.key !== "Enter" && event.key !== " ") return;
                    event.preventDefault();
                    setExpanded((v) => !v);
                }}
            >
                <Repeat
                    size={14}
                    className="shrink-0 theme-accent-text"
                    style={getThemeAccentStyle("for")}
                />
                <span className="text-[11px] font-semibold font-mono text-foreground truncate">
                    {block.id}
                </span>
                <div className="flex-1" />
                <div className="flex items-center gap-1.5 shrink-0">
                    {block.limit != null && (
                        <span className="text-[9px] text-muted-foreground/60 bg-muted/30 rounded px-1">
                            limit={block.limit}
                        </span>
                    )}
                    {hasIterations && (
                        <button
                            type="button"
                            className="rounded px-1.5 py-0.5 text-[10px] text-muted-foreground/60 hover:bg-muted hover:text-foreground transition-colors"
                            onClick={(event) => {
                                event.stopPropagation();
                                toggleAllIterations();
                            }}
                            title={
                                allIterationsExpanded
                                    ? t("render.for.collapseAllIterations")
                                    : t("render.for.expandAllIterations")
                            }
                        >
                            {allIterationsExpanded
                                ? t("render.for.collapseAll")
                                : t("render.for.expandAll")}
                        </button>
                    )}
                    {(canRunFor || canCancelFor) && (
                        <button
                            type="button"
                            className={cn(
                                "flex items-center justify-center h-5 w-5 rounded transition-colors duration-150",
                                status === "running"
                                    ? "theme-accent-icon-button"
                                    : "text-muted-foreground hover:bg-muted hover:text-foreground",
                            )}
                            style={status === "running" ? getThemeAccentStyle("error") : undefined}
                            onClick={(event) => {
                                event.stopPropagation();
                                if (status === "running") {
                                    doc.onCancelFor?.(block.id);
                                    return;
                                }
                                doc.onRunFor?.(block.id);
                            }}
                            disabled={status === "running" ? !canCancelFor : !canRunFor}
                            title={status === "running" ? t("common.stop") : t("render.action.run")}
                            aria-label={status === "running" ? t("common.stop") : t("render.action.run")}
                        >
                            {status === "running" ? (
                                <Square size={10} fill="currentColor" />
                            ) : (
                                <Play size={12} fill="currentColor" />
                            )}
                        </button>
                    )}
                </div>
                <span className="text-muted-foreground shrink-0">
                    {expanded ? (
                        <ChevronDown size={14} />
                    ) : (
                        <ChevronRight size={14} />
                    )}
                </span>
            </div>

            {/* Running progress bar */}
            {status === "running" && forExecution && expanded && (
                <div
                    className="px-3 py-1.5 border-b border-border theme-accent-banner"
                    style={getThemeAccentStyle("for")}
                >
                    <div className="w-full bg-muted rounded-full h-1.5 overflow-hidden">
                        <div
                            className="theme-accent-fill h-full rounded-full transition-all duration-300"
                            style={{
                                ...getThemeAccentStyle("for"),
                                width:
                                    forExecution.totalIterations > 0
                                        ? `${(forExecution.completedIterations / forExecution.totalIterations) * 100}%`
                                        : "0%",
                            }}
                        />
                    </div>
                </div>
            )}

            {/* Body */}
            {expanded && (
                <div>
                    {status === "idle" && (
                        <div className="px-3 py-3 text-[11px] text-muted-foreground/60 text-center">
                            <ForRenderEmptyState
                                block={block}
                                canRun={canRunFor}
                                onRun={() => doc.onRunFor?.(block.id)}
                                isLoading={isLoadingDeferredOutput}
                                errorMessage={deferredOutputError}
                            />
                        </div>
                    )}

                    {hasIterations && (
                        <div className="overflow-hidden">
                            {forExecution.iterations.map((iter) => (
                                <ForIteration
                                    key={iter.index}
                                    iteration={iter}
                                    iterationBlocks={block.children}
                                    eachVar={block.each}
                                    expanded={
                                        !collapsedIterationIndexes.has(
                                            iter.index,
                                        )
                                    }
                                    onToggle={() => toggleIteration(iter.index)}
                                />
                            ))}
                        </div>
                    )}

                    {(status === "error" || deferredOutputError) && (
                        <div className="px-3 py-2">
                            <div
                                className="text-[11px] rounded border px-2 py-1.5 theme-accent-banner"
                                style={getThemeAccentStyle("error")}
                            >
                                {deferredOutputError ?? forExecution?.error}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );

    return (
        <div className="relative my-2 group/for-block">
            <div
                ref={renderContainerRef}
                className={cn(
                    "relative magic-render-unit group/for-render",
                    isFullscreen
                        ? "fixed z-[9000] m-0 flex flex-col overflow-hidden bg-preview-surface p-4 text-foreground"
                        : undefined,
                )}
                style={fullscreenContainerStyle}
                data-render-fullscreen={isFullscreen ? "true" : undefined}
                data-render-fullscreen-scope={
                    isFullscreen ? "workspace" : undefined
                }
            >
                <div
                    ref={renderBodyRef}
                    className={cn(
                        "magic-render-body",
                        isFullscreen
                            ? "magic-render-fullscreen-content"
                            : undefined,
                    )}
                    data-render-fullscreen-width-preset={
                        isFullscreen ? fullscreenWidthPreset : undefined
                    }
                    data-md-rendered-output-hash={outputHash}
                    style={renderBodyStyle}
                >
                    {isFullscreen && (
                        <>
                            <button
                                type="button"
                                role="separator"
                                aria-orientation="vertical"
                                aria-label={t("render.fullscreen.resizeLeft")}
                                title={t("render.fullscreen.resizeTitle")}
                                className="group/resize absolute inset-y-0 left-0 z-20 w-2 -translate-x-3 cursor-col-resize border-0 bg-transparent p-0 outline-none"
                                onMouseDown={(event) =>
                                    startFullscreenWidthResize("left", event)
                                }
                                onDoubleClick={resetFullscreenWidth}
                            >
                                <span
                                    aria-hidden="true"
                                    className={cn(
                                        "absolute inset-y-0 left-1/2 w-px -translate-x-1/2 transition-colors",
                                        isFullscreenWidthDragging
                                            ? "bg-accent"
                                            : "bg-transparent group-hover/resize:bg-accent group-focus-visible/resize:bg-accent",
                                    )}
                                />
                            </button>
                            <button
                                type="button"
                                role="separator"
                                aria-orientation="vertical"
                                aria-label={t("render.fullscreen.resizeRight")}
                                title={t("render.fullscreen.resizeTitle")}
                                className="group/resize absolute inset-y-0 right-0 z-20 w-2 translate-x-3 cursor-col-resize border-0 bg-transparent p-0 outline-none"
                                onMouseDown={(event) =>
                                    startFullscreenWidthResize("right", event)
                                }
                                onDoubleClick={resetFullscreenWidth}
                            >
                                <span
                                    aria-hidden="true"
                                    className={cn(
                                        "absolute inset-y-0 left-1/2 w-px -translate-x-1/2 transition-colors",
                                        isFullscreenWidthDragging
                                            ? "bg-accent"
                                            : "bg-transparent group-hover/resize:bg-accent group-focus-visible/resize:bg-accent",
                                    )}
                                />
                            </button>
                        </>
                    )}
                    {isFullscreen ? (
                        <div className="magic-render-fullscreen-content-inner">
                            {forBlockFrame}
                        </div>
                    ) : (
                        forBlockFrame
                    )}
                </div>

                {doc.executable && (
                    <RenderUnitFooterDisclosure
                        autoOpen={
                            status === "running" ||
                            isLoadingDeferredOutput ||
                            !!deferredOutputError ||
                            isImportRunning ||
                            justFinished
                        }
                        footerClassName={cn(
                            "magic-render-footer flex h-7 min-w-0 items-center gap-2 px-2 text-[10px] text-muted-foreground",
                            isFullscreen
                                ? "sticky bottom-0 z-20 -mx-4 mt-2 shrink-0 border-t border-border bg-preview-surface/95 backdrop-blur"
                                : undefined,
                        )}
                        isFullscreen={isFullscreen}
                        toggleLabel={block.id}
                        >
                            <div className="magic-render-footer-leading flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
                                {status === "running" ||
                                isLoadingDeferredOutput ||
                                isImportRunning ? (
                                    <>
                                        <span
                                            className="inline-block h-2.5 w-2.5 shrink-0 rounded-full border-[1.5px] border-t-transparent animate-spin"
                                            style={{
                                                borderColor:
                                                    getThemeAccentValue(isImportRunning ? "running" : "for"),
                                                borderTopColor: "transparent",
                                            }}
                                        />
                                        <span
                                            className="magic-render-footer-message theme-accent-text min-w-0 max-w-[200px] truncate whitespace-nowrap"
                                            style={getThemeAccentStyle(isImportRunning ? "running" : "for")}
                                        >
                                            {isImportRunning ? (
                                                t("render.importRunning", {
                                                    namespace: importNamespace ?? "",
                                                })
                                            ) : isLoadingDeferredOutput ? (
                                                t("render.placeholder.loadingOutput")
                                            ) : (
                                                <>
                                                    {t("status.running")}{" "}
                                                    {forExecution?.completedIterations ?? 0}
                                                    /{forExecution?.totalIterations ?? 0}
                                                </>
                                            )}
                                        </span>
                                    </>
                                ) : status === "success" && forExecution?.meta ? (
                                    <span
                                        className="magic-render-footer-status inline-flex shrink-0 items-center gap-1 whitespace-nowrap theme-accent-text"
                                        style={getThemeAccentStyle("success")}
                                    >
                                        <svg
                                            aria-hidden="true"
                                            width="11"
                                            height="11"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <polyline points="20 6 9 17 4 12" />
                                        </svg>
                                        <span>{t("render.status.ran")}</span>
                                        <span>
                                            {forExecution.meta.succeeded}/
                                            {forExecution.meta.total}
                                        </span>
                                        {forExecution.meta.failed > 0 && (
                                            <span
                                                className="theme-accent-text"
                                                style={getThemeAccentStyle("error")}
                                            >
                                                {t("render.for.failedCount", {
                                                    count: forExecution.meta.failed,
                                                })}
                                            </span>
                                        )}
                                    </span>
                                ) : status === "error" || deferredOutputError ? (
                                    <span
                                        className="magic-render-footer-message theme-accent-text min-w-0 max-w-[200px] truncate whitespace-nowrap"
                                        style={getThemeAccentStyle("error")}
                                    >
                                        {deferredOutputError
                                            ? t("render.placeholder.outputLoadFailedShort")
                                            : t("render.status.failed")}
                                    </span>
                                ) : (
                                    <span className="magic-render-footer-status shrink-0 whitespace-nowrap text-muted-foreground/70">
                                        {t("render.status.notRun")}
                                    </span>
                                )}
                                {forExecution?.duration != null &&
                                    status !== "running" &&
                                    !isLoadingDeferredOutput &&
                                    !isImportRunning && (
                                        <span className="magic-render-footer-secondary shrink-0 whitespace-nowrap text-muted-foreground/60">
                                            {forExecution.duration}ms
                                        </span>
                                    )}
                                <span className="magic-render-footer-plugin min-w-0 max-w-[160px] truncate whitespace-nowrap text-muted-foreground/40 font-mono">
                                    {block.id} · each={block.each}
                                </span>
                            </div>
                        <div
                            className="magic-render-footer-actions ml-auto flex shrink-0 items-center gap-1"
                            data-plugin-render-footer-actions="true"
                        >
                            <span className="contents magic-render-footer-action-secondary magic-render-footer-action-annotations">
                                <AnnotationBlockFooterControl
                                    blockId={`render:${block.id}`}
                                    label={block.id}
                                    outputHash={outputHash}
                                />
                            </span>
                            <UnitFooterCopyButton
                                primaryText={copyReferenceText}
                                actions={copyActions}
                                className="magic-render-footer-action-secondary magic-render-footer-action-copy"
                            />
                            {createElement(UnitDependencyFooterButton, { targetId: block.id })}
                            {canRunImport && (
                                <button
                                    type="button"
                                    className={cn(
                                        "magic-render-footer-action-secondary magic-render-footer-action-import flex h-5 w-5 items-center justify-center rounded transition-colors duration-150",
                                        isImportRunning
                                            ? "cursor-wait text-muted-foreground/50"
                                            : "cursor-pointer text-muted-foreground hover:bg-muted hover:text-foreground",
                                    )}
                                    onClick={handleRunImport}
                                    disabled={isImportRunning}
                                    title={isImportRunning
                                        ? t("render.importRunning", {
                                            namespace: importNamespace ?? "",
                                        })
                                        : t("render.rerunImport", {
                                            namespace: importNamespace ?? "",
                                        })}
                                    aria-label={isImportRunning
                                        ? t("render.importRunning", {
                                            namespace: importNamespace ?? "",
                                        })
                                        : t("render.rerunImport", {
                                            namespace: importNamespace ?? "",
                                        })}
                                >
                                    {isImportRunning ? (
                                        <Loader2 size={11} className="animate-spin" aria-hidden="true" />
                                    ) : (
                                        <RefreshCw size={11} aria-hidden="true" />
                                    )}
                                </button>
                            )}
                            {isFullscreen && (
                                <div className="magic-render-footer-action-secondary magic-render-footer-action-fullscreen-width relative">
                                    <button
                                        type="button"
                                        className="flex items-center justify-center h-5 min-w-5 rounded px-1 text-muted-foreground hover:bg-muted hover:text-foreground active:bg-muted transition-colors"
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            setIsFullscreenWidthMenuOpen(
                                                (open) => !open,
                                            );
                                        }}
                                        title={t("render.fullscreen.adjustWidthTitle", {
                                            label: currentFullscreenWidthLabel,
                                        })}
                                        aria-label={t("render.fullscreen.adjustWidth")}
                                        aria-haspopup="menu"
                                        aria-expanded={
                                            isFullscreenWidthMenuOpen
                                        }
                                    >
                                        <StretchHorizontal size={11} />
                                    </button>
                                    {isFullscreenWidthMenuOpen && (
                                        <div
                                            role="menu"
                                            className="absolute right-0 bottom-6 z-30 w-28 overflow-hidden rounded-md border border-border bg-background py-1 text-[11px] shadow-lg"
                                        >
                                            {PREVIEW_WIDTH_PRESETS.map(
                                                (preset) => (
                                                    <button
                                                        key={preset.id}
                                                        type="button"
                                                        role="menuitemradio"
                                                        aria-checked={
                                                            fullscreenWidthPreset ===
                                                            preset.id
                                                        }
                                                        className={cn(
                                                            "flex w-full items-center justify-between px-2 py-1 text-left transition-colors hover:bg-muted",
                                                            fullscreenWidthPreset ===
                                                                preset.id
                                                                ? "text-foreground"
                                                                : "text-muted-foreground",
                                                        )}
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            handleFullscreenWidthPresetSelect(
                                                                preset.id,
                                                            );
                                                        }}
                                                    >
                                                        <span>
                                                            {t(`previewWidth.${preset.id}`)}
                                                        </span>
                                                        <span className="font-mono text-[10px] text-muted-foreground/60">
                                                            {preset.width ===
                                                            "fill"
                                                                ? "100%"
                                                                : preset.width}
                                                        </span>
                                                    </button>
                                                ),
                                            )}
                                            {fullscreenWidthPreset ===
                                                "custom" &&
                                                customFullscreenWidth && (
                                                    <div className="border-t border-border px-2 py-1 text-muted-foreground">
                                                        {t("previewWidth.customWithWidth", {
                                                            width: Math.round(
                                                                customFullscreenWidth,
                                                            ),
                                                        })}
                                                    </div>
                                                )}
                                            <button
                                                type="button"
                                                role="menuitem"
                                                className="flex w-full items-center px-2 py-1 text-left text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    resetFullscreenWidth();
                                                }}
                                            >
                                                {t("previewWidth.reset")}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                            <button
                                type="button"
                                className="magic-render-footer-action-secondary magic-render-footer-action-fullscreen flex items-center justify-center h-5 w-5 rounded text-muted-foreground hover:bg-muted hover:text-foreground active:bg-muted transition-colors"
                                onClick={handleToggleFullscreen}
                                title={
                                    isFullscreen
                                        ? t("render.fullscreen.exit")
                                        : t("render.fullscreen.enter")
                                }
                                aria-label={
                                    isFullscreen
                                        ? t("render.fullscreen.exit")
                                        : t("render.fullscreen.enter")
                                }
                            >
                                {isFullscreen ? (
                                    <Minimize2 size={11} />
                                ) : (
                                    <Maximize2 size={11} />
                                )}
                            </button>
                            {(canRunFor || canCancelFor) && (
                                <button
                                    type="button"
                                    className={cn(
                                        "magic-render-footer-action-primary magic-render-footer-action-run flex items-center justify-center h-5 w-5 rounded transition-colors duration-150",
                                        status === "running"
                                            ? "theme-accent-icon-button"
                                            : "text-muted-foreground hover:bg-muted hover:text-foreground cursor-pointer",
                                    )}
                                    style={
                                        status === "running"
                                            ? getThemeAccentStyle("error")
                                            : undefined
                                    }
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        if (status === "running") {
                                            doc.onCancelFor?.(block.id);
                                            return;
                                        }
                                        doc.onRunFor?.(block.id);
                                    }}
                                    disabled={
                                        status === "running"
                                            ? !canCancelFor
                                            : !canRunFor
                                    }
                                    title={
                                        status === "running" ? t("common.stop") : t("render.action.run")
                                    }
                                    aria-label={
                                        status === "running" ? t("common.stop") : t("render.action.run")
                                    }
                                >
                                    {status === "running" ? (
                                        <Square size={10} fill="currentColor" />
                                    ) : (
                                        <Play size={12} fill="currentColor" />
                                    )}
                                </button>
                            )}
                        </div>
                    </RenderUnitFooterDisclosure>
                )}
            </div>
        </div>
    );
}

function ForRenderEmptyState({
    block,
    canRun,
    onRun,
    isLoading = false,
    errorMessage,
}: {
    block: ForBlockType;
    canRun: boolean;
    onRun: () => void;
    isLoading?: boolean;
    errorMessage?: string | null;
}) {
    const t = useT("editor");
    const hasError = !!errorMessage;
    return (
        <div
            className={cn(
                "my-1.5 rounded-lg border border-dashed bg-background px-3 py-2.5 text-left",
                isLoading || hasError ? "theme-accent-banner" : "border-border",
            )}
            style={
                isLoading
                    ? getThemeAccentStyle("running")
                    : hasError
                        ? getThemeAccentStyle("error")
                        : undefined
            }
            data-for-render-empty="true"
        >
            <div className="flex min-w-0 items-center gap-3">
                <RenderUnitBadge main="F" />
                <div className="min-w-0 flex-1">
                    <div className="flex min-w-0 items-center gap-2">
                        <span className="truncate font-mono text-[13px] font-medium text-foreground">
                            {block.id}
                        </span>
                        <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                            {isLoading
                                ? t("status.loading")
                                : hasError
                                    ? t("render.status.loadFailed")
                                    : t("render.status.notRun")}
                        </span>
                    </div>
                    <div className="mt-0.5 flex min-w-0 items-center gap-1.5 text-[11px] text-muted-foreground">
                        <span className="shrink-0">{t("render.kind.forLoop")}</span>
                        <span className="text-muted-foreground/40">·</span>
                        <span className="min-w-0 truncate font-mono text-muted-foreground/50">
                            each={block.each}
                        </span>
                    </div>
                    <div
                        className="mt-1 truncate text-[11px] text-muted-foreground/70"
                        title={t("render.for.sourceTitle", { source: block.of })}
                    >
                        {t("render.for.source")}:
                        {" "}
                        <span className="font-mono">{block.of}</span>
                    </div>
                </div>
                {isLoading ? (
                    <Loader2
                        size={16}
                        className="shrink-0 animate-spin theme-accent-text"
                        style={getThemeAccentStyle("running")}
                    />
                ) : canRun && (
                    <button
                        type="button"
                        data-magic-md-input-confirm="true"
                        className="inline-flex h-7 shrink-0 items-center gap-1.5 rounded-md border border-border bg-primary px-2.5 text-[11px] font-medium text-primary-foreground transition-colors hover:bg-primary/90"
                        onClick={(event) => {
                            event.stopPropagation();
                            onRun();
                        }}
                        aria-label={t("render.runUnit", { id: block.id })}
                    >
                        <Play size={13} aria-hidden="true" />
                        <span>{t("render.action.run")}</span>
                    </button>
                )}
            </div>
        </div>
    );
}

/* ── ForIteration — 单次迭代：ScopeProvider + BlockList 递归 ── */

export function getForIterationClassName(index: number): string {
    return index % 2 === 0 ? "bg-background" : "bg-muted/25";
}

export function getForIterationRailClassName(): string {
    return "absolute inset-x-0 top-0 h-1.5";
}

export function shouldRenderForIterationRail(index: number): boolean {
    return index > 0;
}

export function getForIterationRailStyle(index: number): CSSProperties {
    const stripe =
        index % 2 === 0
            ? "color-mix(in srgb, var(--color-border) 70%, transparent)"
            : "color-mix(in srgb, var(--color-border) 92%, transparent)";
    const base =
        index % 2 === 0
            ? "color-mix(in srgb, var(--color-border) 18%, transparent)"
            : "color-mix(in srgb, var(--color-border) 30%, transparent)";
    return {
        backgroundImage: `repeating-linear-gradient(135deg, ${stripe} 0 6px, ${base} 6px 12px)`,
    };
}

function ForIteration({
    iteration,
    iterationBlocks,
    eachVar,
    expanded,
    onToggle,
}: {
    iteration: ForExecutionEntry["iterations"][number];
    iterationBlocks: ForBlockType["children"];
    eachVar: string;
    expanded: boolean;
    onToggle: () => void;
}) {
    const t = useT("editor");
    return (
        <div
            className={`relative ${getForIterationClassName(iteration.index)}`}
        >
            {shouldRenderForIterationRail(iteration.index) && (
                <span
                    aria-hidden="true"
                    className={getForIterationRailClassName()}
                    style={getForIterationRailStyle(iteration.index)}
                />
            )}
            {/* Iteration header */}
            <button
                type="button"
                className="group flex items-center gap-2 w-full px-3 py-1.5 text-left hover:bg-muted/25 transition-colors"
                onClick={onToggle}
            >
                {expanded ? (
                    <ChevronDown
                        size={11}
                        className="text-muted-foreground/55 shrink-0"
                    />
                ) : (
                    <ChevronRight
                        size={11}
                        className="text-muted-foreground/55 shrink-0"
                    />
                )}
                <span className="w-5 shrink-0 text-right text-[10px] font-mono tabular-nums text-muted-foreground/45">
                    {String(iteration.index + 1).padStart(2, "0")}
                </span>
            </button>

            {/* Expanded: render children lazily so large iteration outputs don't block preview open. */}
            {expanded && iteration.success && (
                <LazyForIterationBody>
                    <ForIterationContent
                        iteration={iteration}
                        iterationBlocks={iterationBlocks}
                        eachVar={eachVar}
                    />
                </LazyForIterationBody>
            )}

            {/* Error */}
            {expanded && !iteration.success && (
                <div className="px-3 pb-2">
                    <div
                        className="text-[11px] rounded border px-2 py-1.5 theme-accent-banner"
                        style={getThemeAccentStyle("error")}
                    >
                        {iteration.error ?? t("render.status.failed")}
                    </div>
                </div>
            )}
        </div>
    );
}

function LazyForIterationBody({
    children,
    estimatedHeight = 160,
}: {
    children: ReactNode;
    estimatedHeight?: number;
}) {
    const ref = useRef<HTMLDivElement>(null);
    const measuredHeight = useRef(0);
    const hasRendered = useRef(false);
    const [visible, setVisible] = useState(
        () => typeof IntersectionObserver === "undefined",
    );

    useEffect(() => {
        const el = ref.current;
        if (!el || typeof IntersectionObserver === "undefined") {
            hasRendered.current = true;
            setVisible(true);
            return;
        }

        let idleHandle: number | undefined;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    if (typeof requestIdleCallback !== "undefined") {
                        idleHandle = requestIdleCallback(() => {
                            hasRendered.current = true;
                            setVisible(true);
                        });
                    } else {
                        hasRendered.current = true;
                        setVisible(true);
                    }
                    return;
                }

                if (
                    idleHandle != null &&
                    typeof cancelIdleCallback !== "undefined"
                ) {
                    cancelIdleCallback(idleHandle);
                    idleHandle = undefined;
                }
                const height = el.offsetHeight;
                if (height > 0) measuredHeight.current = height;
                if (!hasRendered.current) setVisible(false);
            },
            { rootMargin: "500px 0px" },
        );
        observer.observe(el);
        return () => {
            observer.disconnect();
            if (
                idleHandle != null &&
                typeof cancelIdleCallback !== "undefined"
            ) {
                cancelIdleCallback(idleHandle);
            }
        };
    }, []);

    return (
        <div
            ref={ref}
            style={
                visible
                    ? undefined
                    : { minHeight: measuredHeight.current || estimatedHeight }
            }
        >
            {visible ? children : null}
        </div>
    );
}

function ForIterationContent({
    iteration,
    iterationBlocks,
    eachVar,
}: {
    iteration: ForExecutionEntry["iterations"][number];
    iterationBlocks: ForBlockType["children"];
    eachVar: string;
}) {
    const doc = useDocContext();

    const preprocessed = useMemo(
        () =>
            substituteLoopVars(
                iterationBlocks,
                eachVar,
                iteration.item,
                iteration.index,
            ),
        [iterationBlocks, eachVar, iteration.item, iteration.index],
    );

    const scopeDefs = useMemo(
        () =>
            new Map<string, PluginDefinition>([
                ...doc.definitions,
                ...(Object.entries(iteration.definitions ?? {}) as [
                    string,
                    PluginDefinition,
                ][]),
            ]),
        [doc.definitions, iteration.definitions],
    );

    const scopeContentDefs = useMemo(
        () =>
            new Map<string, ContentDefinition>([
                ...(doc.contentDefinitions ?? new Map()),
                ...(Object.entries(iteration.contentDefinitions ?? {}) as [
                    string,
                    ContentDefinition,
                ][]),
            ]),
        [doc.contentDefinitions, iteration.contentDefinitions],
    );

    const scopeForDefs = useMemo(
        () => collectScopedForDefinitions(preprocessed),
        [preprocessed],
    );

    return (
        <ScopeProvider
            definitions={scopeDefs}
            forDefinitions={scopeForDefs}
            contentDefinitions={scopeContentDefs}
            unitOutputs={iteration.outputs as Record<string, PluginOutput>}
            loopVar={{
                name: eachVar,
                item: iteration.item,
                index: iteration.index,
            }}
        >
            <BlockList
                blocks={preprocessed}
                className="magic-render-inner-block-list preview-content px-3 pb-2 prose prose-sm max-w-none"
                blockWrapper={renderInnerBlockItem}
            />
        </ScopeProvider>
    );
}

/* ── ForBlockInlineState — inline 模式显示 ── */

function ForBlockInlineState({
    block,
    forExecution,
    executable,
    onRunFor,
    onCancelFor,
}: {
    block: ForBlockType;
    forExecution: ForExecutionEntry | undefined;
    executable?: boolean;
    onRunFor?: (forId: string) => void;
    onCancelFor?: (forId: string) => void;
}) {
    const status = forExecution?.status ?? "idle";

    if (status === "running" && forExecution) {
        return (
            <span
                className="inline-flex items-center gap-1 text-xs font-mono px-1.5 py-0.5 rounded theme-accent-chip"
                style={getThemeAccentStyle("for")}
            >
                <span
                    className="inline-block h-2.5 w-2.5 rounded-full border-[1.5px] border-t-transparent animate-spin"
                    style={{
                        borderColor: getThemeAccentValue("for"),
                        borderTopColor: "transparent",
                    }}
                />
                <span>{block.id}</span>
                <span className="text-muted-foreground/50">
                    {forExecution.completedIterations}/
                    {forExecution.totalIterations}
                </span>
                {executable && onCancelFor && (
                    <button
                        type="button"
                        className="ml-0.5 border-none bg-transparent cursor-pointer p-0 theme-accent-text"
                        style={getThemeAccentStyle("error")}
                        onClick={() => onCancelFor(block.id)}
                    >
                        ■
                    </button>
                )}
            </span>
        );
    }

    if (status === "success" && forExecution?.meta) {
        return (
            <span
                className="inline-flex items-center gap-1 text-xs font-mono px-1.5 py-0.5 rounded theme-accent-chip"
                style={getThemeAccentStyle("success")}
            >
                <Repeat size={10} />
                {block.id}
                <span>{forExecution.meta.succeeded}✓</span>
                {forExecution.meta.failed > 0 && (
                    <span
                        className="theme-accent-text"
                        style={getThemeAccentStyle("error")}
                    >
                        {forExecution.meta.failed}✗
                    </span>
                )}
            </span>
        );
    }

    if (status === "error") {
        return (
            <span
                className="inline-flex items-center gap-1 text-xs font-mono px-1.5 py-0.5 rounded theme-accent-chip"
                style={getThemeAccentStyle("error")}
            >
                ⚠ {block.id}
            </span>
        );
    }

    return (
        <span className="inline-flex items-center gap-1 text-xs font-mono px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
            <Repeat size={10} />
            {block.id}
            {executable && onRunFor && (
                <button
                    type="button"
                    className="text-primary hover:text-primary/80 ml-0.5 border-none bg-transparent cursor-pointer p-0"
                    onClick={() => onRunFor(block.id)}
                >
                    ▶
                </button>
            )}
        </span>
    );
}
